import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { navigate } from 'gatsby';
import { includes } from 'ramda';
import React, { FC, ReactNode, useCallback, useContext, useState } from 'react';
import { Controller, ControllerRenderProps, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { z } from 'zod';

import BaseCard from '@ui/atoms/BaseCard';
import BasicText from '@ui/atoms/BasicText';
import FlexColumn from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import SelectableButton from '@ui/atoms/SelectableButton';
import SelectableButtonGroup from '@ui/atoms/SelectableButtonGroup';
import StyledLink from '@ui/atoms/StyledLink';
import TextLink from '@ui/atoms/TextLink';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';
import SubmitButton from '@ui/molecules/SubmitButton';

import { medicationDeliveryConstants } from '@ui/routes/shared/MedicationDeliveryStep';
import PharmacySelection from '@ui/routes/shared/PharmacySelectionStep';

import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import { exists } from '@utils/general';

import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import { IMemberData } from '@folx-types/member';

import UpdateMemberDefaultMedicationDeliveryMethod from '@graphql/mutations/UpdateMemberDefaultMedicationDeliveryMethod.gql';

import theme from '@theme/FolxTheme';

import { schema } from './MedicationDeliveryEditStep.data';

export interface IUpdateMemberDefaultMedicationDeliveryMethodArguments {
	input: {
		member: {
			authenticatedMember: boolean;
		};
		medicationDeliveryMethod: string;
		pharmacy: {
			id: string;
		} | null;
	};
}

interface IFolxShippingContent {
	shippingHeader: string;
	shippingDescription: string;
	membershipPrice: string;
	membershipPriceInterval: string;
	medicationPrice: string;
	medicationPriceInterval: string;
	enabled: boolean;
}

interface ICapsuleDeliveryContent {
	capsuleHeader: string;
	capsuleDescription: string;
	membershipPrice: string;
	membershipPriceInterval: string;
	copayPrice: string;
	copayPriceDescription: string;
	enabled: boolean;
	capsuleLogo: string;
}

export interface IPreferredPharmacyContent {
	pharmacyHeader: string;
	pharmacyDescription: string;
}

export interface IMedicationDeliveryEditStepPageContent {
	heading: string;
	subHeading: string;
	capsuleDisclaimer: string;
	capsuleInfoLinkText: string;
	capsuleInfoLinkUrl: string;
	folxShipping: IFolxShippingContent;
	capsuleDelivery: ICapsuleDeliveryContent;
	preferredPharmacy: IPreferredPharmacyContent;
}

interface IMedicationDeliveryEditStepProps {
	pageContent: IMedicationDeliveryEditStepPageContent;
	availableDeliveryMethods?: string[];
	currentDeliveryMethod?: string;
	member?: IMemberData;
}

const SpacedHeadingText = spaced(HeadingText);
const SpacedBasicText = spaced(BasicText);
const SpacedGridItem = fullWidth(spaced(CenteredGridItem));

const CapsuleLogoWrapper = styled.div`
	margin-bottom: ${theme.spacing.lg};
	width: 13rem;

	img {
		width: 100%;
	}
`;

const InsurancePill = styled.div`
	display: inline-flex;
	background: ${theme.colors.yellow};
	border-radius: ${theme.radius.xl};
	padding: 0.6rem 0.9rem;
	margin-top: 3rem;
	color: ${theme.colors.black};
	width: max-content;

	p {
		font-family: 'Montserrat';
	}
`;

const FooterContent = styled(FlexColumn)`
	align-items: flex-start;

	${TextLink}:first-child {
		margin-bottom: ${theme.spacing.md};
	}
`;

const CenteredLink = styled(StyledLink)`
	align-self: center;
`;

const SpacedFooterContent = spaced(FooterContent);

const MedicationDeliveryEditStep: FC<IMedicationDeliveryEditStepProps> = ({
	availableDeliveryMethods,
	currentDeliveryMethod,
	member,
	pageContent,
}) => {
	const {
		heading,
		subHeading,
		capsuleDisclaimer,
		capsuleInfoLinkText,
		capsuleInfoLinkUrl,
		folxShipping,
		capsuleDelivery,
		preferredPharmacy,
	} = pageContent;

	const [submitting, setSubmitting] = useState(false);
	const [isSelectingPharmacy, setIsSelectingPharmacy] = useState(false);

	type Schema = Partial<z.input<typeof schema>>;

	const form = useForm<Schema>({
		resolver: zodResolver(schema),
		mode: 'onChange',
		defaultValues: {
			[medicationDeliveryConstants.MEDICATION_DELIVERY_METHOD]: currentDeliveryMethod,
		},
	});

	const { handleSubmit, control, watch, register, unregister, setValue, getValues } = form;

	const deliveryMethodData = watch(medicationDeliveryConstants.MEDICATION_DELIVERY_METHOD);

	const preferredPharmacyFieldsWrapper = useCallback(
		(fields: Partial<ControllerRenderProps<Schema>>) => ({
			...fields,
			onChange() {
				setIsSelectingPharmacy(true);
				register(medicationDeliveryConstants.PHARMACY_ID);
			},
		}),
		[register]
	);

	const onPreferredPharmacySelectionCancelled = useCallback(() => {
		unregister(medicationDeliveryConstants.PHARMACY_ID);
		setIsSelectingPharmacy(false);
	}, [unregister]);

	const { client: graphQLv2Client } = useContext(GraphQLv2Context);

	const [updateMemberDefaultMedicationDeliveryMethod] = useMutation<
		void,
		IUpdateMemberDefaultMedicationDeliveryMethodArguments
	>(UpdateMemberDefaultMedicationDeliveryMethod, {
		client: graphQLv2Client,
		fetchPolicy: 'network-only',
	});

	const onSubmit = useCallback(async () => {
		setSubmitting(true);
		const data = getValues();

		console.log({ data });

		const input = {
			member: {
				authenticatedMember: true,
			},
			medicationDeliveryMethod: data?.[
				medicationDeliveryConstants.MEDICATION_DELIVERY_METHOD
			] as string,
			pharmacy: data?.[medicationDeliveryConstants.PHARMACY_ID]
				? {
						id: data?.[medicationDeliveryConstants.PHARMACY_ID] as string,
				  }
				: null,
		};

		await updateMemberDefaultMedicationDeliveryMethod({ variables: { input } });

		if (input?.medicationDeliveryMethod === medicationDeliveryConstants.PHARMACY_METHOD) {
			navigate('./post_submit_preferred_pharmacy');
		} else {
			navigate('./post_submit');
		}

		setSubmitting(false);
	}, [getValues, updateMemberDefaultMedicationDeliveryMethod]);

	const onPreferredPharmacySelected = useCallback(
		async (formData: Record<string, unknown>) => {
			setValue(
				medicationDeliveryConstants.MEDICATION_DELIVERY_METHOD,
				medicationDeliveryConstants.PHARMACY_METHOD
			);
			setValue(
				medicationDeliveryConstants.PHARMACY_ID,
				(formData[medicationDeliveryConstants.PHARMACY_ID] as string) ?? ''
			);
			await onSubmit();
			setIsSelectingPharmacy(false);
		},
		[setValue, onSubmit]
	);

	const getFolxDeliveryCard = (content: IFolxShippingContent): ReactNode => {
		const headerContent = <BasicText variant="field">{content.shippingHeader}</BasicText>;

		const bodyContent = (
			<WYSIWYGContent dangerouslySetInnerHTML={{ __html: content?.shippingDescription }} />
		);

		return <BaseCard header={headerContent} body={bodyContent} borderless />;
	};

	const getCapsuleDeliveryCard = (content: ICapsuleDeliveryContent): ReactNode => {
		const headerContent = (
			<div>
				<CapsuleLogoWrapper>
					<img src={content.capsuleLogo} alt="capsule logo"></img>
				</CapsuleLogoWrapper>
				<BasicText variant="field">{content.capsuleHeader}</BasicText>
			</div>
		);

		const bodyContent = (
			<WYSIWYGContent dangerouslySetInnerHTML={{ __html: content?.capsuleDescription }} />
		);

		const footerContent = (
			<InsurancePill>
				<p>Insurance accepted</p>
			</InsurancePill>
		);

		return <BaseCard header={headerContent} body={bodyContent} footer={footerContent} borderless />;
	};

	const getPreferredPharmacyCard = (content: IPreferredPharmacyContent): ReactNode => {
		const headerContent = <BasicText variant="field">{content?.pharmacyHeader}</BasicText>;

		const bodyContent = (
			<WYSIWYGContent dangerouslySetInnerHTML={{ __html: content?.pharmacyDescription }} />
		);

		const footerContent = (
			<InsurancePill>
				<p>Insurance accepted</p>
			</InsurancePill>
		);

		return <BaseCard header={headerContent} body={bodyContent} footer={footerContent} borderless />;
	};

	if (isSelectingPharmacy) {
		return (
			<PharmacySelection
				stepKey="PHARMACY_SELECTION"
				path="pharmacy_selection"
				secure
				onPharmacySelection={onPreferredPharmacySelected}
				onCancel={onPreferredPharmacySelectionCancelled}
			/>
		);
	}

	return (
		<>
			<SpacedGridItem marginBottom="lgxl">
				<SpacedHeadingText marginBottom="sm">{heading}</SpacedHeadingText>
				<BasicText>{subHeading}</BasicText>
			</SpacedGridItem>
			<SpacedGridItem marginBottom="xxxl">
				<form onSubmit={handleSubmit(onSubmit)}>
					<Controller
						name={medicationDeliveryConstants.MEDICATION_DELIVERY_METHOD}
						control={control}
						render={({ field: { value, ...field } }) => {
							console.log(field);

							return (
								<SelectableButtonGroup
									layout="column"
									aria-label={medicationDeliveryConstants.MEDICATION_DELIVERY_METHOD}>
									{includes(
										medicationDeliveryConstants.FOLX_METHOD,
										availableDeliveryMethods ?? []
									) && (
										<SelectableButton
											alignment={'start'}
											optionValue={medicationDeliveryConstants.FOLX_METHOD}
											optionLabel={folxShipping.shippingHeader}
											key={medicationDeliveryConstants.FOLX_METHOD}
											value={value ?? ''}
											{...field}>
											{getFolxDeliveryCard(folxShipping)}
										</SelectableButton>
									)}
									{includes(
										medicationDeliveryConstants.CAPSULE_METHOD,
										availableDeliveryMethods ?? []
									) && (
										<SelectableButton
											alignment={'start'}
											optionValue={medicationDeliveryConstants.CAPSULE_METHOD}
											optionLabel={capsuleDelivery.capsuleHeader}
											key={medicationDeliveryConstants.CAPSULE_METHOD}
											value={value ?? ''}
											{...field}>
											{getCapsuleDeliveryCard(capsuleDelivery)}
										</SelectableButton>
									)}
									<SelectableButton
										alignment={'start'}
										optionValue={medicationDeliveryConstants.PHARMACY_METHOD}
										optionLabel={preferredPharmacy.pharmacyHeader}
										key={medicationDeliveryConstants.PHARMACY_METHOD}
										value={value ?? ''}
										{...preferredPharmacyFieldsWrapper(field)}>
										{getPreferredPharmacyCard(preferredPharmacy)}
									</SelectableButton>
								</SelectableButtonGroup>
							);
						}}
					/>
					<SpacedFooterContent marginTop="lg">
						<TextLink label={capsuleInfoLinkText} href={capsuleInfoLinkUrl} variant="bold" newTab />
						<SpacedBasicText variant="fineprint" marginBottom="xxl">
							{capsuleDisclaimer}
						</SpacedBasicText>
					</SpacedFooterContent>
					<FlexColumn gap="lg">
						<SubmitButton
							variant="secondary"
							label="Save"
							submitting={submitting}
							disabled={
								exists(currentDeliveryMethod) && currentDeliveryMethod === deliveryMethodData
							}
						/>
						<CenteredLink variant="underline" to="..">
							Cancel
						</CenteredLink>
					</FlexColumn>
				</form>
			</SpacedGridItem>
		</>
	);
};

export default MedicationDeliveryEditStep;
